import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spin, Tag, Empty, notification, Modal, Upload } from 'antd';
import {
  CalendarOutlined, CameraOutlined, DeleteOutlined, DownloadOutlined, EditOutlined,
  EnvironmentOutlined, EyeOutlined, GiftOutlined, HeartOutlined, HomeOutlined,
  IdcardOutlined, PhoneOutlined, PictureOutlined, PlusOutlined, ShopOutlined,
  StarOutlined, TeamOutlined, TrophyOutlined, UserOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, countryFlag, inrToForeign, ssInvalidate, isBirthdayToday } from '../shared/utils';
import { ProfilePhotoUpload } from '../components/CropModal';
import { MembershipCard } from '../components/MembershipCard';
import { Section, InfoRow, InfoGrid, SectionHeader, EditableField } from '../components/InfoComponents';
// Note: SectionBar and FField are defined locally below as they were co-located in the original Profile section

const fmt = (v) => v || '—';
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const fmtAmount = (a) => Number(a || 0).toLocaleString('en-IN');

const RELATION_OPTIONS = ['पुत्र श्री','पुत्री श्री','पत्नी श्री','पति श्री','स्वयं','अन्य'];
const MARITAL_OPTIONS  = ['Married','Unmarried','Widowed','Divorced'];


// ── Shared form helpers — defined OUTSIDE ProfileTab to avoid remount on re-render
export function SectionBar({ title, icon, editing: isEditing, onEdit, onCancel, onSave, saving: isSaving }) {
  return (
    <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon}
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
      </div>
      {!isEditing
        ? <button onClick={onEdit} style={{ fontSize: 11, color: M, background: 'none', border: `1px solid ${M}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}><EditOutlined style={{ marginRight: 4 }} />Edit</button>
        : <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>रद्द करें</button>
            <button onClick={onSave} disabled={isSaving} style={{ fontSize: 11, color: '#fff', background: M, border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>{isSaving ? 'सहेज रहे हैं…' : 'सहेजें'}</button>
          </div>
      }
    </div>
  );
}

export function FField({ label, value, onChange, type = 'text', options }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3, fontWeight: 600 }}>{label}</div>
      {options
        ? <select value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 13, color: '#111827' }}>
            <option value="">— चुनें —</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        : <input type={type} value={value} onChange={e => onChange(e.target.value)}
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 13, color: '#111827', boxSizing: 'border-box' }} />
      }
    </div>
  );
}

const EMPTY_FAM_FORM = {
  m_name: '', m_relation_type: '', m_gender: '', m_dob: '', m_married_status: '', m_education: '',
  m_occupation: '', m_current_address: '', m_phone_no: '', m_anniversary_date: '',
};

export function ProfileTab({ profile, onUpdated }) {
  const [occupation,    setOccupation]    = useState([]);
  const [occLoaded,     setOccLoaded]     = useState(false);
  const [contactsCount, setContactsCount] = useState(null);
  // ── Family (inline management) ──
  const [famMembers,    setFamMembers]    = useState([]);
  const [famLoading,    setFamLoading]    = useState(true);
  const [famModal,      setFamModal]      = useState(false);
  const [famEditId,     setFamEditId]     = useState(null);
  const [famSaving,     setFamSaving]     = useState(false);
  const [famForm,       setFamForm]       = useState(EMPTY_FAM_FORM);
  // Family images: newly picked file + preview, and whether the saved image should be removed
  const [famPhoto,      setFamPhoto]      = useState({ file: null, preview: null, remove: false });
  const [famAnnivImg,   setFamAnnivImg]   = useState({ file: null, preview: null, remove: false });

  // ── Social Status (inline management) ──
  const [socialItems,   setSocialItems]   = useState([]);
  const [socialLoading, setSocialLoading] = useState(true);
  const [socialModal,   setSocialModal]   = useState(false);
  const [socialEditId,  setSocialEditId]  = useState(null);
  const [socialSaving,  setSocialSaving]  = useState(false);
  const [socialForm,    setSocialForm]    = useState({ sn_name:'', sn_organisation_name:'', sn_duration_start:'', sn_duration_end:'', sn_organisation_address:'', org_phone_no:'' });

  // ── Personal info edit ──
  const [editingInfo,   setEditingInfo]   = useState(false);
  const [savingInfo,    setSavingInfo]    = useState(false);
  const [infoData,      setInfoData]      = useState({});

  // ── Contact edit ──
  const [editingContact, setEditingContact] = useState(false);
  const [savingContact,  setSavingContact]  = useState(false);
  const [contactData,    setContactData]    = useState({});

  // ── Perm address edit ──
  const [editingPerm,   setEditingPerm]   = useState(false);
  const [savingPerm,    setSavingPerm]    = useState(false);
  const [permData,      setPermData]      = useState({});
  const [villageSearch, setVillageSearch] = useState('');
  const [villageList,   setVillageList]   = useState([]);
  const [villageLoading,setVillageLoading]= useState(false);
  const villDebRef = useState(null);

  // ── Photo ──
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // ── Anniversary image ──
  const [uploadingAnniv,   setUploadingAnniv]   = useState(false);
  const annivInputRef = useRef(null);
  const [editingAnnivDate, setEditingAnnivDate] = useState(false);
  const [annivDateInput,   setAnnivDateInput]   = useState('');
  const [savingAnnivDate,  setSavingAnnivDate]  = useState(false);
  // undefined=use prop value, null=optimistically removed, string=optimistically uploaded URL
  const [localAnnivImg,    setLocalAnnivImg]    = useState(undefined);
  const [localAnnivDate,   setLocalAnnivDate]   = useState(undefined); // undefined=use prop, string=optimistically saved

  // ── Camera capture ──
  const [cameraOpen,    setCameraOpen]    = useState(false);
  const [cameraStream,  setCameraStream]  = useState(null);
  const [capturedImg,   setCapturedImg]   = useState(null); // base64 preview
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${baseUrl}/member/occupation`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setOccupation(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setOccLoaded(true); });
    axios.get(`${baseUrl}/member/family`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setFamMembers(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setFamLoading(false); });
    axios.get(`${baseUrl}/member/social-status`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setSocialItems(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setSocialLoading(false); });
    axios.get(`${baseUrl}/member/contacts`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setContactsCount((r.data.result || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const p = profile;

  // ── Personal info ──────────────────────────────────────────────────────────
  const startEditInfo = () => {
    setInfoData({
      name_hn:          p.name_hn          || '',
      name_eng:         p.name_eng         || '',
      father_name_hn:   p.father_name      || '',
      father_name_en:   p.father_name      || '',
      relation_hn:      p.relation         || '',
      dob:              p.dob ? p.dob.split('T')[0] : '',
      anniversary_date: p.anniversary_date ? p.anniversary_date.split('T')[0] : '',
      maritial_status:  p.marital_status   || '',
      caste:            p.caste            || '',
      gotra:            p.gotra            || '',
      kuldevi:          p.kuldevi          || '',
      kulrishi:         p.kulrishi         || '',
    });
    setEditingInfo(true);
  };

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      const payload = { ...infoData, father_name_en: infoData.father_name_hn, relation_en: infoData.relation_hn };
      const res = await axios.patch(`${baseUrl}/member/profile`, payload, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: 'जानकारी अपडेट हुई!', placement: 'topRight' });
        setEditingInfo(false); onUpdated();
      }
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
    finally { setSavingInfo(false); }
  };

  // ── Contact ───────────────────────────────────────────────────────────────
  const startEditContact = () => {
    setContactData({ email_address: p.email || '', alternate_phone_no: p.alt_phone_1 || '', alternate_phone_no2: p.alt_phone_2 || '' });
    setEditingContact(true);
  };
  const saveContact = async () => {
    setSavingContact(true);
    try {
      const res = await axios.patch(`${baseUrl}/member/profile`, contactData, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: 'Contact updated!', placement: 'topRight' }); setEditingContact(false); onUpdated(); }
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
    finally { setSavingContact(false); }
  };

  // ── Perm address ──────────────────────────────────────────────────────────
  const startEditPerm = () => {
    setPermData({
      per_village_hn: p.perm_address?.village || '', per_village_en: p.perm_address?.village || '',
      per_tahsil_hn:  p.perm_address?.tahsil  || '', per_tahsil_en:  p.perm_address?.tahsil  || '',
      per_district_hn:p.perm_address?.district|| '', per_district_en:p.perm_address?.district|| '',
      per_state_hn:   p.perm_address?.state   || '', per_state_en:   p.perm_address?.state   || '',
      per_pin_code_en:p.perm_address?.zip     || '',
    });
    setVillageSearch(p.perm_address?.village || '');
    setEditingPerm(true);
  };

  const searchVillage = (val) => {
    setVillageSearch(val);
    if (villDebRef[0]) clearTimeout(villDebRef[0]);
    if (!val.trim()) { setVillageList([]); return; }
    setVillageLoading(true);
    villDebRef[0] = setTimeout(async () => {
      try {
        const r = await axios.get(`${baseUrl}/member/village-list?search=${encodeURIComponent(val)}&limit=20`, { headers: authHeader() });
        setVillageList(r.data.result || []);
      } catch {} finally { setVillageLoading(false); }
    }, 400);
  };

  const selectVillage = (v) => {
    setPermData(d => ({
      ...d,
      per_village_hn: v.name_hn || v.name_en, per_village_en: v.name_en || v.name_hn,
      per_tahsil_hn:  v.tehsil_hn || v.tehsil_en || '', per_tahsil_en: v.tehsil_en || v.tehsil_hn || '',
      per_district_hn:v.district_hn || v.district_en || '', per_district_en: v.district_en || v.district_hn || '',
      per_state_hn:   v.state_hn || v.state_en || '', per_state_en: v.state_en || v.state_hn || '',
      per_pin_code_en:v.pincode_en || '',
    }));
    setVillageSearch(v.name_hn || v.name_en);
    setVillageList([]);
  };

  const savePerm = async () => {
    setSavingPerm(true);
    try {
      const res = await axios.patch(`${baseUrl}/member/perm-address`, permData, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: 'पता अपडेट हुआ!', placement: 'topRight' }); setEditingPerm(false); onUpdated(); }
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
    finally { setSavingPerm(false); }
  };

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { notification.warning({ message: 'Photo 5MB से छोटी होनी चाहिए', placement: 'topRight' }); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append('profile_img', file);
      const res = await axios.post(`${baseUrl}/member/profile-photo`, fd, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: 'Photo अपडेट हुई!', placement: 'topRight' }); onUpdated(); }
      else { notification.error({ message: res.data.message || 'Photo upload failed', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Photo upload failed', placement: 'topRight' }); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  // ── Anniversary image upload / remove ─────────────────────────────────────
  const saveAnnivImage = async (file) => {
    setUploadingAnniv(true);
    try {
      const fd = new FormData();
      if (file) fd.append('anniversary_img', file);
      const res = await axios.post(`${baseUrl}/member/anniversary-image`, fd, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: file ? 'वर्षगांठ फोटो अपडेट हुई!' : 'वर्षगांठ फोटो हटाई गई', placement: 'topRight' });
        setLocalAnnivImg(res.data.anniversary_img ?? null);
        onUpdated();
      } else {
        notification.error({ message: res.data.message || 'Upload failed', placement: 'topRight' });
      }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploadingAnniv(false); }
  };

  const handleAnnivChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { notification.warning({ message: 'कृपया फोटो (image) चुनें', placement: 'topRight' }); return; }
    if (file.size > 5 * 1024 * 1024) { notification.warning({ message: 'Photo 5MB से छोटी होनी चाहिए', placement: 'topRight' }); return; }
    saveAnnivImage(file);
  };

  // ── Camera handlers ───────────────────────────────────────────────────────
  const openCamera = async () => {
    setCapturedImg(null);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 640 }, audio: false });
      setCameraStream(stream);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch {
      notification.error({ message: 'Camera access नहीं मिली। Permission देवें।', placement: 'topRight' });
      setCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
    setCameraOpen(false);
    setCapturedImg(null);
  };

  const capturePhoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const ox = (video.videoWidth  - size) / 2;
    const oy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, ox, oy, size, size, 0, 0, size, size);
    setCapturedImg(canvas.toDataURL('image/jpeg', 0.85));
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
  };

  const uploadCaptured = async () => {
    if (!capturedImg) return;
    setUploadingPhoto(true);
    try {
      const blob = await fetch(capturedImg).then(r => r.blob());
      const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const fd   = new FormData(); fd.append('profile_img', file);
      const res  = await axios.post(`${baseUrl}/member/profile-photo`, fd, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: 'Photo अपडेट हुई!', placement: 'topRight' });
        stopCamera(); onUpdated();
      }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploadingPhoto(false); }
  };

  // ── Birthday poster modal — must be before early return (Rules of Hooks)
  const [bdayModal,     setBdayModal]     = useState(false);
  const [bdayPosterUrl, setBdayPosterUrl] = useState(null);
  const [bdayLoading,   setBdayLoading]   = useState(false);

  const birthday = isBirthdayToday(profile?.dob);

  useEffect(() => {
    if (!birthday) return;
    setBdayModal(true);
    setBdayLoading(true);
    const token = localStorage.getItem('memberToken');
    fetch(`${baseUrl}/member/birthday-poster`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { setBdayPosterUrl(URL.createObjectURL(blob)); })
      .catch(() => notification.warning({ message: 'Birthday poster not available yet.', placement: 'topRight' }))
      .finally(() => setBdayLoading(false));
  }, []); // eslint-disable-line

  const downloadPoster = () => {
    if (!bdayPosterUrl) return;
    const a = document.createElement('a');
    a.href = bdayPosterUrl; a.download = 'birthday_poster.jpg'; a.click();
  };

  const sharePoster = async () => {
    if (!bdayPosterUrl) return;
    try {
      const blob = await fetch(bdayPosterUrl).then(r => r.blob());
      const file = new File([blob], 'birthday_poster.jpg', { type: 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'जन्मदिन की शुभकामनाएँ', text: `${profile?.name_hn || profile?.name} जी को जन्मदिन की हार्दिक शुभकामनाएँ!` });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(`*जन्मदिन की हार्दिक शुभकामनाएँ!*\n${profile?.name_hn || profile?.name} जी को बधाई`)}`, '_blank');
      }
    } catch {}
  };

  // Early return after all hooks
  if (!profile) return <Empty description="Profile not found" />;
  const hasPerm = p.perm_address && Object.values(p.perm_address).some(Boolean);

  return (
    <div>
      {/* Birthday banner */}
      {birthday && (
        <div style={{ background: 'linear-gradient(135deg, #7c3aed, #db2777)', borderRadius: 12, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <GiftOutlined style={{ fontSize: 24, color: '#fde68a', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>जन्मदिन की शुभकामनाएँ!</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>आपका Birthday Poster तैयार है</div>
          </div>
          {/* Quick download — always accessible even without opening modal */}
          {bdayPosterUrl && (
            <button onClick={downloadPoster} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <DownloadOutlined /> Download
            </button>
          )}
          <button onClick={() => setBdayModal(true)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <EyeOutlined /> View
          </button>
        </div>
      )}

      {/* ── Birthday Poster Modal ── */}
      {bdayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 18, overflow: 'hidden', width: '100%', maxWidth: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #7c3aed, #db2777)', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>जन्मदिन की शुभकामनाएँ!</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>आपका Birthday Poster तैयार है</div>
              </div>
              <button onClick={() => setBdayModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 28, height: 28, color: '#fff', fontSize: 14, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Poster preview */}
            <div style={{ background: '#f3f4f6', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {bdayLoading
                ? <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /><div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>Poster बन रहा है…</div></div>
                : bdayPosterUrl
                  ? <img src={bdayPosterUrl} alt="Birthday Poster" style={{ width: '100%', display: 'block' }} />
                  : <div style={{ padding: 32, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>Poster अभी available नहीं</div>
              }
            </div>

            {/* Action buttons */}
            {bdayPosterUrl && (
              <div style={{ padding: '14px 16px', display: 'flex', gap: 10 }}>
                <button onClick={downloadPoster} style={{
                  flex: 1, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '11px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <DownloadOutlined /> Download
                </button>
                <button onClick={sharePoster} style={{
                  flex: 1, background: '#25d366', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '11px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <WhatsAppOutlined /> Share
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, display: 'flex', alignItems: 'stretch' }}>
          {/* Left — name + tags on gradient */}
          <div style={{ flex: 1, padding: '18px 16px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{fmt(p.name_hn)}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>{p.name_eng}</div>
            <div style={{ display: 'flex', gap: 5, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 20, padding: '2px 9px' }}>ABRSVS सदस्य</span>
              {p.membership_no && <span style={{ fontSize: 10, fontWeight: 800, background: G, color: M, borderRadius: 20, padding: '2px 9px' }}>#{p.membership_no}</span>}
              {p.status && <span style={{ fontSize: 10, background: p.status === 'Active' ? '#dcfce7' : 'rgba(255,255,255,0.15)', color: p.status === 'Active' ? '#15803d' : '#fff', borderRadius: 20, padding: '2px 9px' }}>{p.status}</span>}
            </div>
            {p.dob && <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 4 }}><CalendarOutlined />{fmtDate(p.dob)}</div>}
            {contactsCount !== null && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <TeamOutlined style={{ fontSize: 12 }} />
                <span style={{ fontWeight: 700 }}>{contactsCount}</span> मित्र
              </div>
            )}
          </div>
          {/* Right — square photo with Add/Update button */}
          <div style={{ width: 110, flexShrink: 0, position: 'relative' }}>
            {p.photo
              ? <img src={p.photo} alt={p.name}
                  style={{ width: '100%', height: '100%', minHeight: 110, objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', minHeight: 110, background: 'rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800, color: G }}>
                  {(p.name||'?').trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                </div>}
            {/* Single Add/Update button → expands to Gallery + Camera */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)' }}>
              {uploadingPhoto
                ? <div style={{ padding: '7px 0', textAlign: 'center' }}><Spin size="small" /></div>
                : <div style={{ display: 'flex' }}>
                    <button onClick={() => photoInputRef.current?.click()}
                      style={{ flex: 1, background: 'none', border: 'none', borderRight: '1px solid rgba(255,255,255,0.25)', color: '#fff', padding: '7px 0', fontSize: 9, cursor: 'pointer', fontWeight: 700, lineHeight: 1.4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <PictureOutlined style={{ fontSize: 13 }} />Gallery
                    </button>
                    <button onClick={openCamera}
                      style={{ flex: 1, background: 'none', border: 'none', color: '#fff', padding: '7px 0', fontSize: 9, cursor: 'pointer', fontWeight: 700, lineHeight: 1.4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CameraOutlined style={{ fontSize: 13 }} />Camera
                    </button>
                  </div>
              }
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
          </div>
        </div>
      </div>

      {/* Personal Information — editable */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <SectionBar title="Personal Information — व्यक्तिगत जानकारी" icon={<UserOutlined style={{ color: M, fontSize: 13 }} />}
          editing={editingInfo} onEdit={startEditInfo} onCancel={() => setEditingInfo(false)} onSave={saveInfo} saving={savingInfo} />
        <div style={{ padding: 16 }}>
          {editingInfo ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 16px' }}>
              <FField label="पूरा नाम (हिंदी) *"   value={infoData.name_hn}         onChange={v => setInfoData(d=>({...d, name_hn: v}))} />
              <FField label="Full Name (English) *" value={infoData.name_eng}        onChange={v => setInfoData(d=>({...d, name_eng: v}))} />
              <FField label="पिता का नाम *"         value={infoData.father_name_hn}  onChange={v => setInfoData(d=>({...d, father_name_hn: v, father_name_en: v}))} />
              <FField label="Relation"              value={infoData.relation_hn}     onChange={v => setInfoData(d=>({...d, relation_hn: v}))} options={RELATION_OPTIONS} />
              <FField label="जन्म तिथि"             value={infoData.dob}             onChange={v => setInfoData(d=>({...d, dob: v}))} type="date" />
              <FField label="वैवाहिक स्थिति"        value={infoData.maritial_status} onChange={v => setInfoData(d=>({...d, maritial_status: v}))} options={MARITAL_OPTIONS} />
              <FField label="विवाह वर्षगांठ (Anniversary)" value={infoData.anniversary_date} onChange={v => setInfoData(d=>({...d, anniversary_date: v}))} type="date" />
              <FField label="जाति (Caste)"          value={infoData.caste}           onChange={v => setInfoData(d=>({...d, caste: v}))} />
              <FField label="गोत्र"                 value={infoData.gotra}           onChange={v => setInfoData(d=>({...d, gotra: v}))} />
              <FField label="कुलदेवी"               value={infoData.kuldevi}         onChange={v => setInfoData(d=>({...d, kuldevi: v}))} />
              <FField label="कुलऋषि"               value={infoData.kulrishi}        onChange={v => setInfoData(d=>({...d, kulrishi: v}))} />
            </div>
          ) : (
            <InfoGrid fields={[
              { label: 'पूरा नाम (हिंदी)',    value: p.name_hn },
              { label: 'Full Name',            value: p.name_eng },
              { label: 'पिता का नाम',          value: p.father_name },
              { label: 'Relation',             value: p.relation },
              { label: 'जन्म तिथि',            value: fmtDate(p.dob) },
              { label: 'वैवाहिक स्थिति',       value: p.marital_status },
              { label: 'विवाह वर्षगांठ',        value: fmtDate(p.anniversary_date) },
              { label: 'जाति (Caste)',          value: p.caste },
              { label: 'गोत्र',                value: p.gotra },
              { label: 'कुलदेवी',              value: p.kuldevi },
              { label: 'कुलऋषि',              value: p.kulrishi },
              { label: 'Category',             value: p.category },
            ]} />
          )}
        </div>
      </div>

      {/* Anniversary image — sent on WhatsApp on the anniversary date */}
      {(() => {
        const annivImg  = localAnnivImg  !== undefined ? localAnnivImg  : p.anniversary_img;
        const annivDate = localAnnivDate !== undefined ? localAnnivDate : p.anniversary_date;
        return (p.marital_status === 'Married' || annivDate || annivImg) && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
          <SectionHeader title="Anniversary — विवाह वर्षगांठ" icon={<HeartOutlined />} />
          <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {/* Anniversary image */}
            <div style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {uploadingAnniv
                ? <Spin size="small" />
                : annivImg
                  ? <img src={annivImg} alt="Anniversary" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 26 }}>💍</span>}
            </div>

            {/* Text + inline date editor */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingAnnivDate ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    value={annivDateInput}
                    onChange={e => setAnnivDateInput(e.target.value)}
                    style={{ border: '1.5px solid #d1d5db', borderRadius: 7, padding: '4px 8px', fontSize: 12, outline: 'none' }}
                  />
                  <Button size="small" type="primary" loading={savingAnnivDate}
                    disabled={!annivDateInput}
                    onClick={async () => {
                      setSavingAnnivDate(true);
                      try {
                        const res = await axios.patch(`${baseUrl}/member/profile`, { anniversary_date: annivDateInput }, { headers: authHeader() });
                        if (res.data.status) {
                          notification.success({ message: 'वर्षगांठ तिथि सहेजी गई!', placement: 'topRight' });
                          setLocalAnnivDate(annivDateInput);
                          setEditingAnnivDate(false);
                          onUpdated();
                        } else {
                          notification.error({ message: res.data.message || 'Save failed', placement: 'topRight' });
                        }
                      } catch { notification.error({ message: 'Save failed', placement: 'topRight' }); }
                      finally { setSavingAnnivDate(false); }
                    }}
                    style={{ borderRadius: 7, background: '#15803d', borderColor: '#15803d' }}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingAnnivDate(false)} style={{ borderRadius: 7 }}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                    {annivDate ? fmtDate(annivDate) : 'वर्षगांठ तिथि नहीं जोड़ी'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>
                    {annivDate ? 'इस तिथि पर WhatsApp शुभकामना भेजी जाएगी।' : 'तिथि जोड़ें →'}
                  </div>
                </>
              )}
            </div>

            {/* Action buttons — top-right corner, compact */}
            {!editingAnnivDate && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setAnnivDateInput(annivDate ? annivDate.split('T')[0] : ''); setEditingAnnivDate(true); }}
                  style={{ borderRadius: 7, borderColor: '#1d4ed8', color: '#1d4ed8', background: '#eff6ff', fontWeight: 600 }}
                >
                  {annivDate ? 'तिथि बदलें' : 'तिथि जोड़ें'}
                </Button>
                <Button size="small" icon={<PictureOutlined />} loading={uploadingAnniv}
                  onClick={() => annivInputRef.current?.click()}
                  style={{ borderRadius: 7, background: M, borderColor: M, color: G, fontWeight: 600 }}>
                  {annivImg ? 'फोटो बदलें' : 'फोटो जोड़ें'}
                </Button>
                {annivImg && (
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={uploadingAnniv}
                    onClick={() => saveAnnivImage(null)} style={{ borderRadius: 7 }}>
                    हटाएं
                  </Button>
                )}
              </div>
            )}
            <input ref={annivInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAnnivChange} />
          </div>
        </div>
        );
      })()}

      {/* Contact & Identity — EDITABLE */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <SectionBar title="Contact & Identity" icon={<PhoneOutlined style={{ color: M, fontSize: 13 }} />}
          editing={editingContact} onEdit={startEditContact} onCancel={() => setEditingContact(false)} onSave={saveContact} saving={savingContact} />
        <div style={{ padding: 16 }}>
          {editingContact ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px 20px' }}>
              <FField label="Email" value={contactData.email_address} onChange={v => setContactData(d=>({...d, email_address: v}))} type="email" />
              <FField label="Alt Phone 1" value={contactData.alternate_phone_no} onChange={v => setContactData(d=>({...d, alternate_phone_no: v}))} type="tel" />
              <FField label="Alt Phone 2" value={contactData.alternate_phone_no2} onChange={v => setContactData(d=>({...d, alternate_phone_no2: v}))} type="tel" />
            </div>
          ) : (
            <InfoGrid fields={[
              { label: 'Phone',         value: p.phone },
              { label: 'Email',         value: p.email },
              { label: 'Alt Phone 1',   value: p.alt_phone_1 },
              { label: 'Alt Phone 2',   value: p.alt_phone_2 },
              { label: 'PAN Number',    value: p.pan_no },
              { label: 'Aadhar No.',    value: p.aadhar_no },
              { label: 'Joining Date',  value: fmtDate(p.joining_date) },
            ]} />
          )}
        </div>
      </div>

      {/* Permanent Address — editable with village master */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <SectionBar title="Permanent Address — स्थायी पता" icon={<HomeOutlined style={{ color: M, fontSize: 13 }} />}
          editing={editingPerm} onEdit={startEditPerm} onCancel={() => setEditingPerm(false)} onSave={savePerm} saving={savingPerm} />
        <div style={{ padding: 16 }}>
          {editingPerm ? (
            <div>
              {/* Village search autocomplete */}
              <div style={{ marginBottom: 14, position: 'relative' }}>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3, fontWeight: 600 }}>गाँव खोजें (Village Master) *</div>
                <input
                  value={villageSearch}
                  onChange={e => searchVillage(e.target.value)}
                  placeholder="गाँव / ग्राम का नाम लिखें…"
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                />
                {villageLoading && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>खोज रहे हैं…</div>}
                {villageList.length > 0 && (
                  <div style={{ position: 'absolute', zIndex: 100, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', top: '100%', left: 0, right: 0, maxHeight: 220, overflowY: 'auto' }}>
                    {villageList.map(v => (
                      <div key={v.id} onClick={() => selectVillage(v)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ fontWeight: 700, color: '#111827' }}>{v.name_hn || v.name_en}</span>
                        <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 6 }}>{[v.tehsil_hn || v.tehsil_en, v.district_hn || v.district_en].filter(Boolean).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Auto-filled fields (read-only after village select) */}
              {permData.per_district_hn && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px 16px' }}>
                  <FField label="तहसील" value={permData.per_tahsil_hn} onChange={v => setPermData(d=>({...d, per_tahsil_hn: v, per_tahsil_en: v}))} />
                  <FField label="जिला" value={permData.per_district_hn} onChange={v => setPermData(d=>({...d, per_district_hn: v, per_district_en: v}))} />
                  <FField label="राज्य" value={permData.per_state_hn} onChange={v => setPermData(d=>({...d, per_state_hn: v, per_state_en: v}))} />
                  <FField label="PIN Code" value={permData.per_pin_code_en} onChange={v => setPermData(d=>({...d, per_pin_code_en: v}))} />
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280' }}>गाँव चुनने पर तहसील, जिला, राज्य स्वतः भर जाएंगे।</div>
            </div>
          ) : hasPerm ? (
            <InfoGrid fields={[
              { label: 'Village / गाँव',  value: p.perm_address.village },
              { label: 'Tahsil / तहसील', value: p.perm_address.tahsil },
              { label: 'District / जिला', value: p.perm_address.district },
              { label: 'State / राज्य',   value: p.perm_address.state },
              { label: 'PIN Code',         value: p.perm_address.zip },
            ]} />
          ) : (
            <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>स्थायी पता नहीं जोड़ा। Edit पर क्लिक करें।</div>
          )}
        </div>
      </div>

      {/* Temporary Address — editable */}
      <TempAddressSection profile={p} onUpdated={onUpdated} />

      {/* ── Camera Modal ── */}
      {cameraOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#111', borderRadius: 16, overflow: 'hidden', width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            {/* Header */}
            <div style={{ background: '#1a1a1a', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}><CameraOutlined style={{ marginRight: 8 }} />Live Photo</span>
              <button onClick={stopCamera} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Video / Preview */}
            <div style={{ position: 'relative', background: '#000', aspectRatio: '1/1' }}>
              {!capturedImg
                ? <video ref={videoRef} autoPlay playsInline muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scaleX(-1)' }} />
                : <img src={capturedImg} alt="preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              }
              {/* Face guide circle */}
              {!capturedImg && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ width: '65%', height: '65%', border: '2px dashed rgba(255,215,0,0.6)', borderRadius: '50%' }} />
                </div>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Buttons */}
            <div style={{ padding: '16px', display: 'flex', gap: 10 }}>
              {!capturedImg ? (
                <>
                  <button onClick={stopCamera}
                    style={{ flex: 1, background: '#374151', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    रद्द करें
                  </button>
                  <button onClick={capturePhoto}
                    style={{ flex: 2, background: M, color: G, border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    📸 खींचें
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => { setCapturedImg(null); openCamera(); }}
                    style={{ flex: 1, background: '#374151', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    फिर से
                  </button>
                  <button onClick={uploadCaptured} disabled={uploadingPhoto}
                    style={{ flex: 2, background: '#15803d', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    {uploadingPhoto ? <Spin size="small" /> : '✓ Use This Photo'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Business summary */}
      {p.business && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
          <SectionHeader title="Business Listing — व्यवसाय" icon={<ShopOutlined />} />
          <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>{p.business.firm_name || 'Business Listing'}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                <EnvironmentOutlined style={{ marginRight: 4 }} />
                {[p.business.current_city, p.business.district].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
            <Tag color={p.business.verification_status === 'Verified' ? 'green' : 'orange'}>
              {p.business.verification_status === 'Verified' ? '✓ Verified' : '⏳ Pending'}
            </Tag>
          </div>
        </div>
      )}

      {/* Occupation — नौकरी / पेशा (linked from SN user, read-only) */}
      {occLoaded && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
          <SectionHeader title="Occupation — नौकरी / पेशा" icon={<IdcardOutlined />} />
          <div style={{ padding: 14 }}>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#1e40af', display: 'flex', gap: 6 }}>
              <IdcardOutlined style={{ flexShrink: 0, marginTop: 1 }} />
              <span>यह आपकी नौकरी / सेवा की जानकारी है (जैसे: सरकारी, प्राइवेट)। दुकान / व्यवसाय के लिए <strong>My Business</strong> tab देखें।</span>
            </div>
            {occupation.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '14px 0', fontSize: 12, color: '#9ca3af' }}>
                <IdcardOutlined style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
                कोई नौकरी / पेशा जानकारी नहीं — ABRSVS से संपर्क करें।
              </div>
            ) : occupation.map((occ, i) => (
              <div key={occ.id || i} style={{ marginBottom: i < occupation.length - 1 ? 12 : 0, paddingBottom: i < occupation.length - 1 ? 12 : 0, borderBottom: i < occupation.length - 1 ? '1px dashed #f3f4f6' : 'none' }}>
                <InfoGrid fields={[
                  { label: 'पद / नाम',       value: occ.occ_name },
                  { label: 'Field / क्षेत्र', value: occ.occ_field },
                  { label: 'Category',         value: occ.occ_category },
                  { label: 'Locality',         value: occ.occ_locality },
                  { label: 'District',         value: occ.occ_district },
                  { label: 'State',            value: occ.occ_state },
                  { label: 'Office Phone',     value: occ.occ_office_phone_no },
                  { label: 'Mobile',           value: occ.occ_mobile_no },
                ]} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ FAMILY — Full management ══════════════════════════════════════════ */}
      {(() => {
        const iS = { width:'100%', height:38, borderRadius:8, border:'1px solid #d1d5db', padding:'0 12px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
        const lS = { fontSize:11, fontWeight:700, color:'#374151', marginBottom:4 };
        const FF = (k) => (e) => setFamForm(f => ({ ...f, [k]: e.target.value }));
        const resetFamImages = (m) => {
          setFamPhoto({ file: null, preview: m?.m_photo || null, remove: false });
          setFamAnnivImg({ file: null, preview: m?.m_anniversary_img || null, remove: false });
        };
        const openFamAdd  = () => { setFamEditId(null); setFamForm(EMPTY_FAM_FORM); resetFamImages(null); setFamModal(true); };
        const openFamEdit = (m) => {
          setFamEditId(m.id);
          setFamForm(Object.fromEntries(Object.keys(EMPTY_FAM_FORM).map(k => [k, m[k] ? String(m[k]).slice(0, k.includes('date') || k === 'm_dob' ? 10 : undefined) : ''])));
          resetFamImages(m);
          setFamModal(true);
        };
        const pickFamImage = (setter) => (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (!file.type.startsWith('image/')) { notification.warning({ message:'कृपया फोटो (image) चुनें', placement:'topRight' }); return; }
          if (file.size > 5 * 1024 * 1024) { notification.warning({ message:'Photo 5MB से छोटी होनी चाहिए', placement:'topRight' }); return; }
          setter({ file, preview: URL.createObjectURL(file), remove: false });
        };
        const saveFam = async () => {
          if (!famForm.m_name.trim()) { notification.warning({ message:'नाम आवश्यक है', placement:'topRight' }); return; }
          if (famForm.m_phone_no && !/^\d{10,12}$/.test(famForm.m_phone_no)) { notification.warning({ message:'मोबाइल नंबर 10-12 अंकों का होना चाहिए', placement:'topRight' }); return; }
          setFamSaving(true);
          try {
            const fd = new FormData();
            Object.entries(famForm).forEach(([k, v]) => fd.append(k, v ?? ''));
            if (famPhoto.file) fd.append('m_photo', famPhoto.file);
            else if (famPhoto.remove) fd.append('remove_photo', 'true');
            if (famAnnivImg.file) fd.append('m_anniversary_img', famAnnivImg.file);
            else if (famAnnivImg.remove) fd.append('remove_anniversary_img', 'true');
            const res = famEditId
              ? await axios.patch(`${baseUrl}/member/family/${famEditId}`, fd, { headers: authHeader() })
              : await axios.post(`${baseUrl}/member/family`, fd, { headers: authHeader() });
            if (res.data.status) {
              notification.success({ message: famEditId ? 'अपडेट हो गया!' : 'सदस्य जोड़ा गया!', placement:'topRight' });
              setFamModal(false); setFamEditId(null);
              const r2 = await axios.get(`${baseUrl}/member/family`, { headers: authHeader() });
              if (r2.data.status) setFamMembers(r2.data.result || []);
            }
          } catch (e) { notification.error({ message: e.response?.data?.message || 'Failed', placement:'topRight' }); }
          finally { setFamSaving(false); }
        };
        const delFam = async (id) => {
          try {
            await axios.delete(`${baseUrl}/member/family/${id}`, { headers: authHeader() });
            setFamMembers(p => p.filter(m => m.id !== id));
            notification.success({ message:'हटाया गया!', placement:'topRight' });
          } catch { notification.error({ message:'Delete failed', placement:'topRight' }); }
        };
        return (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', marginBottom:14, overflow:'hidden' }}>
            <div style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0', padding:'8px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <TeamOutlined style={{ color:M, fontSize:13 }} />
                <span style={{ fontSize:11.5, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:0.5 }}>परिवार — Family ({famMembers.length})</span>
              </div>
              <Button icon={<PlusOutlined />} size="small" onClick={openFamAdd}
                style={{ borderRadius:8, background:M, borderColor:M, color:G, fontWeight:700 }}>
                सदस्य जोड़ें
              </Button>
            </div>
            <div style={{ padding:'12px 14px' }}>
              {famLoading ? <div style={{ textAlign:'center', padding:20 }}><Spin /></div>
                : !famMembers.length ? (
                  <div style={{ textAlign:'center', padding:'16px 0', color:'#9ca3af' }}>
                    <TeamOutlined style={{ fontSize:28, marginBottom:6, display:'block' }} />
                    <div style={{ fontSize:13 }}>कोई परिवार सदस्य नहीं — "सदस्य जोड़ें" से शुरू करें।</div>
                  </div>
                ) : famMembers.map(m => (
                  <div key={m.id} style={{ background:'#f9fafb', borderRadius:10, border:'1px solid #f0f0f0', marginBottom:10, overflow:'hidden' }}>
                    <div style={{ background:'#f0f2f5', padding:'8px 14px', display:'flex', alignItems:'center', gap:10 }}>
                      {m.m_photo
                        ? <img src={m.m_photo} alt={m.m_name} style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:`2px solid ${M}` }} />
                        : <div style={{ width:36, height:36, borderRadius:'50%', background:M, display:'flex', alignItems:'center', justifyContent:'center', color:G, fontSize:12, fontWeight:800, flexShrink:0 }}>
                            {(m.m_name||'?').trim()[0].toUpperCase()}
                          </div>}
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>{m.m_name||'—'}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>{m.m_relation_type||''}{m.m_gender ? ` · ${m.m_gender}` : ''}</div>
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => openFamEdit(m)} style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:7, padding:'3px 10px', fontSize:11, fontWeight:700, color:'#1d4ed8', cursor:'pointer' }}><EditOutlined /> Edit</button>
                        <button onClick={() => delFam(m.id)} style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'3px 10px', fontSize:11, fontWeight:700, color:'#b91c1c', cursor:'pointer' }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ padding:14 }}>
                      <InfoGrid fields={[
                        { label:'जन्म तिथि', value: fmtDate(m.m_dob) },
                        { label:'मोबाइल',     value: m.m_phone_no },
                        { label:'वैवाहिक',    value: m.m_married_status },
                        { label:'विवाह वर्षगांठ', value: fmtDate(m.m_anniversary_date) },
                        { label:'शिक्षा',     value: m.m_education },
                        { label:'व्यवसाय',    value: m.m_occupation },
                        { label:'पता',        value: m.m_current_address },
                      ]} />
                      {m.m_anniversary_img && (
                        <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, fontSize:11, color:'#6b7280' }}>
                          <img src={m.m_anniversary_img} alt="Anniversary" style={{ width:44, height:44, borderRadius:8, objectFit:'cover', border:'1px solid #e5e7eb' }} />
                          💍 वर्षगांठ फोटो — इस दिन WhatsApp पर भेजी जाएगी
                        </div>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
            <Modal open={famModal} onCancel={() => setFamModal(false)} footer={null} centered width={480}
              title={<span style={{ color:M, fontWeight:700 }}><TeamOutlined style={{ marginRight:8 }} />{famEditId ? 'सदस्य Edit करें' : 'परिवार सदस्य जोड़ें'}</span>}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px', marginBottom:16 }}>
                <div style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:12 }}>
                  {famPhoto.preview
                    ? <img src={famPhoto.preview} alt="" style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', border:`2px solid ${M}` }} />
                    : <div style={{ width:64, height:64, borderRadius:'50%', background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', color:'#9ca3af', fontSize:24 }}><UserOutlined /></div>}
                  <div>
                    <div style={lS}>फोटो (जन्मदिन पोस्टर पर लगेगी)</div>
                    <div style={{ display:'flex', gap:6 }}>
                      <label style={{ background:'#f9fafb', border:'1px solid #d1d5db', borderRadius:7, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        <PictureOutlined /> {famPhoto.preview ? 'बदलें' : 'जोड़ें'}
                        <input type="file" accept="image/*" style={{ display:'none' }} onChange={pickFamImage(setFamPhoto)} />
                      </label>
                      {famPhoto.preview && (
                        <button type="button" onClick={() => setFamPhoto(p => ({ file:null, preview:null, remove: !p.file || Boolean(famEditId) }))}
                          style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'4px 10px', fontSize:12, fontWeight:600, color:'#b91c1c', cursor:'pointer' }}>हटाएं</button>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>नाम *</div><input value={famForm.m_name} onChange={FF('m_name')} placeholder="पूरा नाम" style={iS} /></div>
                <div><div style={lS}>संबंध</div><select value={famForm.m_relation_type} onChange={FF('m_relation_type')} style={iS}><option value="">-- चुनें --</option>{RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                <div><div style={lS}>लिंग</div><select value={famForm.m_gender} onChange={FF('m_gender')} style={iS}><option value="">-- चुनें --</option>{GENDERS.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
                <div><div style={lS}>जन्म तिथि</div><input type="date" value={famForm.m_dob} onChange={FF('m_dob')} style={iS} /></div>
                <div><div style={lS}>वैवाहिक स्थिति</div><select value={famForm.m_married_status} onChange={FF('m_married_status')} style={iS}><option value="">-- चुनें --</option>{MARITAL.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><div style={lS}>मोबाइल (WhatsApp)</div><input value={famForm.m_phone_no} onChange={FF('m_phone_no')} placeholder="10 अंकों का नंबर" inputMode="numeric" maxLength={12} style={iS} /></div>
                <div><div style={lS}>विवाह वर्षगांठ</div><input type="date" value={famForm.m_anniversary_date} onChange={FF('m_anniversary_date')} style={iS} /></div>
                {(famForm.m_anniversary_date || famAnnivImg.preview) && (
                  <div style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:10, background:'#fdf2f8', border:'1px solid #fbcfe8', borderRadius:8, padding:'8px 10px' }}>
                    {famAnnivImg.preview
                      ? <img src={famAnnivImg.preview} alt="" style={{ width:48, height:48, borderRadius:8, objectFit:'cover' }} />
                      : <span style={{ fontSize:26 }}>💍</span>}
                    <div style={{ flex:1, fontSize:11, color:'#9d174d' }}>वर्षगांठ फोटो — इस दिन WhatsApp पर भेजी जाएगी</div>
                    <label style={{ background:'#fff', border:'1px solid #f9a8d4', borderRadius:7, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', color:'#9d174d' }}>
                      {famAnnivImg.preview ? 'बदलें' : 'फोटो जोड़ें'}
                      <input type="file" accept="image/*" style={{ display:'none' }} onChange={pickFamImage(setFamAnnivImg)} />
                    </label>
                    {famAnnivImg.preview && (
                      <button type="button" onClick={() => setFamAnnivImg(p => ({ file:null, preview:null, remove: !p.file || Boolean(famEditId) }))}
                        style={{ background:'#fff', border:'1px solid #fecaca', borderRadius:7, padding:'4px 8px', fontSize:12, color:'#b91c1c', cursor:'pointer' }}>✕</button>
                    )}
                  </div>
                )}
                <div><div style={lS}>शिक्षा</div><input value={famForm.m_education} onChange={FF('m_education')} placeholder="जैसे: B.A., 10वीं" style={iS} /></div>
                <div><div style={lS}>व्यवसाय</div><input value={famForm.m_occupation} onChange={FF('m_occupation')} placeholder="जैसे: किसान, शिक्षक" style={iS} /></div>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>वर्तमान पता</div><input value={famForm.m_current_address} onChange={FF('m_current_address')} placeholder="शहर, जिला, राज्य" style={iS} /></div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <Button block onClick={() => setFamModal(false)} style={{ borderRadius:8, height:42, fontWeight:600, color:'#6b7280', borderColor:'#d1d5db' }}>रद्द करें</Button>
                <Button block loading={famSaving} onClick={saveFam} style={{ borderRadius:8, height:42, fontWeight:700, background:M, borderColor:M, color:G }}>{famEditId ? 'Save करें' : 'जोड़ें'}</Button>
              </div>
            </Modal>
          </div>
        );
      })()}

      {/* ══ SOCIAL STATUS — Full management ════════════════════════════════════ */}
      {(() => {
        const iS = { width:'100%', height:38, borderRadius:8, border:'1px solid #d1d5db', padding:'0 12px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
        const lS = { fontSize:11, fontWeight:700, color:'#374151', marginBottom:4 };
        const FS = (k) => (e) => setSocialForm(f => ({ ...f, [k]: e.target.value }));
        const openSocAdd  = () => { setSocialEditId(null); setSocialForm({ sn_name:'', sn_organisation_name:'', sn_duration_start:'', sn_duration_end:'', sn_organisation_address:'', org_phone_no:'' }); setSocialModal(true); };
        const openSocEdit = (s) => { setSocialEditId(s.id); setSocialForm({ sn_name:s.sn_name||'', sn_organisation_name:s.sn_organisation_name||'', sn_duration_start:s.sn_duration_start?.slice(0,10)||'', sn_duration_end:s.sn_duration_end?.slice(0,10)||'', sn_organisation_address:s.sn_organisation_address||'', org_phone_no:s.org_phone_no||'' }); setSocialModal(true); };
        const saveSoc = async () => {
          if (!socialForm.sn_name.trim()) { notification.warning({ message:'पद / नाम आवश्यक है', placement:'topRight' }); return; }
          setSocialSaving(true);
          try {
            const res = socialEditId
              ? await axios.patch(`${baseUrl}/member/social-status/${socialEditId}`, socialForm, { headers: authHeader() })
              : await axios.post(`${baseUrl}/member/social-status`, socialForm, { headers: authHeader() });
            if (res.data.status) {
              notification.success({ message: socialEditId ? 'अपडेट हो गया!' : 'सामाजिक स्थिति जोड़ी गई!', placement:'topRight' });
              setSocialModal(false); setSocialEditId(null);
              const r2 = await axios.get(`${baseUrl}/member/social-status`, { headers: authHeader() });
              if (r2.data.status) setSocialItems(r2.data.result || []);
            }
          } catch (e) { notification.error({ message: e.response?.data?.message || 'Failed', placement:'topRight' }); }
          finally { setSocialSaving(false); }
        };
        const delSoc = async (id) => {
          try {
            await axios.delete(`${baseUrl}/member/social-status/${id}`, { headers: authHeader() });
            setSocialItems(p => p.filter(s => s.id !== id));
            notification.success({ message:'हटाया गया!', placement:'topRight' });
          } catch { notification.error({ message:'Delete failed', placement:'topRight' }); }
        };
        return (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', marginBottom:14, overflow:'hidden' }}>
            <div style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0', padding:'8px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <TrophyOutlined style={{ color:M, fontSize:13 }} />
                <span style={{ fontSize:11.5, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:0.5 }}>सामाजिक स्थिति — Social Status ({socialItems.length})</span>
              </div>
              <Button icon={<PlusOutlined />} size="small" onClick={openSocAdd}
                style={{ borderRadius:8, background:M, borderColor:M, color:G, fontWeight:700 }}>
                जोड़ें
              </Button>
            </div>
            <div style={{ padding:'12px 14px' }}>
              {socialLoading ? <div style={{ textAlign:'center', padding:20 }}><Spin /></div>
                : !socialItems.length ? (
                  <div style={{ textAlign:'center', padding:'16px 0', color:'#9ca3af' }}>
                    <TrophyOutlined style={{ fontSize:28, marginBottom:6, display:'block' }} />
                    <div style={{ fontSize:13 }}>कोई सामाजिक स्थिति नहीं — "जोड़ें" से शुरू करें।</div>
                  </div>
                ) : socialItems.map(s => (
                  <div key={s.id} style={{ background:'#f9fafb', borderRadius:10, border:'1px solid #f0f0f0', marginBottom:10, overflow:'hidden' }}>
                    <div style={{ background:'#f0f2f5', padding:'8px 14px', display:'flex', alignItems:'center', gap:10 }}>
                      <StarOutlined style={{ color:G, background:M, borderRadius:'50%', padding:6, fontSize:12, flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>{s.sn_name}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>{s.sn_organisation_name||''}</div>
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => openSocEdit(s)} style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:7, padding:'3px 10px', fontSize:11, fontWeight:700, color:'#1d4ed8', cursor:'pointer' }}><EditOutlined /> Edit</button>
                        <button onClick={() => delSoc(s.id)} style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'3px 10px', fontSize:11, fontWeight:700, color:'#b91c1c', cursor:'pointer' }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ padding:14 }}>
                      <InfoGrid fields={[
                        { label:'पद',           value:s.sn_name },
                        { label:'Organisation', value:s.sn_organisation_name },
                        { label:'From',         value:fmtDate(s.sn_duration_start) },
                        { label:'To',           value:fmtDate(s.sn_duration_end) },
                        { label:'Address',      value:s.sn_organisation_address },
                        { label:'Phone',        value:s.org_phone_no },
                      ]} />
                    </div>
                  </div>
                ))
              }
            </div>
            <Modal open={socialModal} onCancel={() => setSocialModal(false)} footer={null} centered width={480}
              title={<span style={{ color:M, fontWeight:700 }}><TrophyOutlined style={{ marginRight:8 }} />{socialEditId ? 'सामाजिक स्थिति Edit करें' : 'सामाजिक स्थिति जोड़ें'}</span>}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px', marginBottom:16 }}>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>पद / नाम *</div><input value={socialForm.sn_name} onChange={FS('sn_name')} placeholder="जैसे: अध्यक्ष, सदस्य, सचिव" style={iS} /></div>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>संस्था / संगठन</div><input value={socialForm.sn_organisation_name} onChange={FS('sn_organisation_name')} placeholder="संस्था का नाम" style={iS} /></div>
                <div><div style={lS}>से (From)</div><input type="date" value={socialForm.sn_duration_start} onChange={FS('sn_duration_start')} style={iS} /></div>
                <div><div style={lS}>तक (To)</div><input type="date" value={socialForm.sn_duration_end} onChange={FS('sn_duration_end')} style={iS} /></div>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>संस्था का पता</div><input value={socialForm.sn_organisation_address} onChange={FS('sn_organisation_address')} placeholder="पता / शहर" style={iS} /></div>
                <div style={{ gridColumn:'1 / -1' }}><div style={lS}>फ़ोन नम्बर</div><input type="tel" value={socialForm.org_phone_no} onChange={FS('org_phone_no')} placeholder="संस्था का फ़ोन नम्बर" style={iS} /></div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <Button block onClick={() => setSocialModal(false)} style={{ borderRadius:8, height:42, fontWeight:600, color:'#6b7280', borderColor:'#d1d5db' }}>रद्द करें</Button>
                <Button block loading={socialSaving} onClick={saveSoc} style={{ borderRadius:8, height:42, fontWeight:700, background:M, borderColor:M, color:G }}>{socialEditId ? 'Save करें' : 'जोड़ें'}</Button>
              </div>
            </Modal>
          </div>
        );
      })()}

    </div>
  );
}

// ── Temp Address editable section ─────────────────────────────────────────────
export function TempAddressSection({ profile, onUpdated }) {
  const p = profile;
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({
    tem_address_en:  p.temp_address?.address  || '',
    tem_city_en:     p.temp_address?.city      || '',
    tem_district_en: p.temp_address?.district  || '',
    tem_state_en:    p.temp_address?.state     || '',
    tem_country_en:  p.temp_address?.country   || '',
    tem_pin_code_en: p.temp_address?.zip       || '',
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${baseUrl}/member/temp-address`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: 'Address updated!', placement: 'topRight' });
        setEditing(false);
        onUpdated();
      }
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
    finally { setSaving(false); }
  };

  const hasData = Object.values(p.temp_address || {}).some(Boolean);

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
      <div style={{
        background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
        padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EnvironmentOutlined style={{ color: M, fontSize: 13 }} />
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>Temporary / Current Address</span>
        </div>
        {!editing
          ? <button onClick={() => setEditing(true)} style={{ fontSize: 11, color: M, background: 'none', border: `1px solid ${M}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
              <EditOutlined style={{ marginRight: 4 }} />Edit
            </button>
          : <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(false)} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ fontSize: 11, color: '#fff', background: M, border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
        }
      </div>
      <div style={{ padding: 16 }}>
        {editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px 20px' }}>
            <EditableField label="Address" value={form.tem_address_en} onChange={v => setForm(f => ({...f, tem_address_en: v}))} />
            <EditableField label="City / Town" value={form.tem_city_en} onChange={v => setForm(f => ({...f, tem_city_en: v}))} />
            <EditableField label="District" value={form.tem_district_en} onChange={v => setForm(f => ({...f, tem_district_en: v}))} />
            <EditableField label="State" value={form.tem_state_en} onChange={v => setForm(f => ({...f, tem_state_en: v}))} />
            <EditableField label="Country" value={form.tem_country_en} onChange={v => setForm(f => ({...f, tem_country_en: v}))} />
            <EditableField label="PIN Code" value={form.tem_pin_code_en} onChange={v => setForm(f => ({...f, tem_pin_code_en: v}))} />
          </div>
        ) : hasData ? (
          <InfoGrid fields={[
            { label: 'Address',   value: p.temp_address.address },
            { label: 'City',      value: p.temp_address.city },
            { label: 'District',  value: p.temp_address.district },
            { label: 'State',     value: p.temp_address.state },
            { label: 'Country',   value: p.temp_address.country },
            { label: 'PIN Code',  value: p.temp_address.zip },
          ]} />
        ) : (
          <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            No temporary address added. Click Edit to add.
          </div>
        )}
      </div>
    </div>
  );
}

export function OccupationTab() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${baseUrl}/member/occupation`, { headers: authHeader() })
      .then(r => { if (r.data.status) setData(r.data.result || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  return (
    <div>
      {/* Clarification note */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#1e40af' }}>
        <IdcardOutlined style={{ marginRight: 6 }} />
        <strong>Occupation</strong> — आपकी नौकरी / पेशे की जानकारी (जैसे: सरकारी नौकरी, प्राइवेट नौकरी आदि)।
        व्यवसाय / दुकान की जानकारी के लिए <strong>My Business</strong> tab देखें।
      </div>
      {!data.length ? (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28, textAlign: 'center' }}>
          <IdcardOutlined style={{ fontSize: 36, color: '#9ca3af', marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>कोई व्यवसाय/नौकरी जानकारी नहीं</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Contact ABRSVS to add occupation details.</div>
        </div>
      ) : data.map((occ, i) => (
        <div key={occ.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 12, overflow: 'hidden' }}>
          <SectionHeader title={`Occupation ${data.length > 1 ? i + 1 : ''}`} icon={<IdcardOutlined />} />
          <div style={{ padding: 16 }}>
            <InfoGrid fields={[
              { label: 'Name / पद',        value: occ.occ_name },
              { label: 'Field / क्षेत्र',  value: occ.occ_field },
              { label: 'Category',          value: occ.occ_category },
              { label: 'Locality',          value: occ.occ_locality },
              { label: 'District',          value: occ.occ_district },
              { label: 'State',             value: occ.occ_state },
              { label: 'Office Phone',      value: occ.occ_office_phone_no },
              { label: 'Mobile',            value: occ.occ_mobile_no },
            ]} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Family Tab ────────────────────────────────────────────────────────────────
const EMPTY_MEMBER = {
  m_name: '', m_relation_type: '', m_gender: '', m_dob: '',
  m_education: '', m_occupation: '', m_current_address: '', m_married_status: '',
};
const RELATIONS = ['पत्नी', 'पति', 'पुत्र', 'पुत्री', 'माता', 'पिता', 'भाई', 'बहन', 'दादा', 'दादी', 'नाना', 'नानी', 'अन्य'];
const GENDERS   = ['पुरुष', 'महिला', 'अन्य'];
const MARITAL   = ['विवाहित', 'अविवाहित', 'विधुर / विधवा', 'तलाकशुदा'];

// ── Social Status Tab (managed inside Profile) ───────────────────────────────
const EMPTY_SOCIAL = {
  sn_name: '', sn_organisation_name: '', sn_duration_start: '',
  sn_duration_end: '', sn_organisation_address: '', org_phone_no: '',
};

export function SocialStatusTab() {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form,      setForm]      = useState(EMPTY_SOCIAL);

  const F = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/social-status`, { headers: authHeader() });
      if (r.data.status) setItems(r.data.result || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd  = () => { setEditId(null); setForm(EMPTY_SOCIAL); setShowModal(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ sn_name: s.sn_name||'', sn_organisation_name: s.sn_organisation_name||'',
              sn_duration_start: s.sn_duration_start?.slice(0,10)||'',
              sn_duration_end: s.sn_duration_end?.slice(0,10)||'',
              sn_organisation_address: s.sn_organisation_address||'', org_phone_no: s.org_phone_no||'' });
    setShowModal(true);
  };

  const saveStatus = async () => {
    if (!form.sn_name.trim()) { notification.warning({ message: 'पद / नाम आवश्यक है', placement: 'topRight' }); return; }
    setSaving(true);
    try {
      const res = editId
        ? await axios.patch(`${baseUrl}/member/social-status/${editId}`, form, { headers: authHeader() })
        : await axios.post(`${baseUrl}/member/social-status`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: editId ? 'अपडेट हो गया!' : 'सामाजिक स्थिति जोड़ी गई!', placement: 'topRight' });
        setShowModal(false); setForm(EMPTY_SOCIAL); setEditId(null); fetchItems();
      } else notification.error({ message: res.data.message || 'Failed', placement: 'topRight' });
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const deleteStatus = async (id) => {
    try {
      await axios.delete(`${baseUrl}/member/social-status/${id}`, { headers: authHeader() });
      notification.success({ message: 'हटाया गया!', placement: 'topRight' });
      fetchItems();
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const inputStyle = { width: '100%', height: 38, borderRadius: 8, border: '1px solid #d1d5db', padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>सामाजिक स्थिति ({items.length})</div>
        <Button icon={<PlusOutlined />} size="small" onClick={openAdd}
          style={{ borderRadius: 8, background: M, borderColor: M, color: G, fontWeight: 700 }}>
          जोड़ें
        </Button>
      </div>

      {!items.length ? (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28, textAlign: 'center' }}>
          <TrophyOutlined style={{ fontSize: 36, color: '#9ca3af', marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>कोई सामाजिक स्थिति नहीं</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>ऊपर "जोड़ें" से सामाजिक पद / स्थिति जोड़ें।</div>
        </div>
      ) : items.map(s => (
        <div key={s.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 10, overflow: 'hidden' }}>
          <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <StarOutlined style={{ color: G, background: M, borderRadius: '50%', padding: 6, fontSize: 12, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{s.sn_name}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{s.sn_organisation_name || ''}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => openEdit(s)} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}>
                <EditOutlined /> Edit
              </button>
              <button onClick={() => deleteStatus(s.id)} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <InfoGrid fields={[
              { label: 'पद',           value: s.sn_name },
              { label: 'Organisation', value: s.sn_organisation_name },
              { label: 'From',         value: fmtDate(s.sn_duration_start) },
              { label: 'To',           value: fmtDate(s.sn_duration_end) },
              { label: 'Address',      value: s.sn_organisation_address },
              { label: 'Phone',        value: s.org_phone_no },
            ]} />
          </div>
        </div>
      ))}

      {/* ── ADD / EDIT MODAL ── */}
      <Modal open={showModal} onCancel={() => { setShowModal(false); setForm(EMPTY_SOCIAL); setEditId(null); }}
        footer={null} centered width={480}
        title={<span style={{ color: M, fontWeight: 700 }}><TrophyOutlined style={{ marginRight: 8 }} />{editId ? 'सामाजिक स्थिति Edit करें' : 'सामाजिक स्थिति जोड़ें'}</span>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>पद / नाम *</div>
            <input value={form.sn_name} onChange={F('sn_name')} placeholder="जैसे: अध्यक्ष, सदस्य, सचिव" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>संस्था / संगठन</div>
            <input value={form.sn_organisation_name} onChange={F('sn_organisation_name')} placeholder="संस्था का नाम" style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>से (From)</div>
            <input type="date" value={form.sn_duration_start} onChange={F('sn_duration_start')} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>तक (To)</div>
            <input type="date" value={form.sn_duration_end} onChange={F('sn_duration_end')} style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>संस्था का पता</div>
            <input value={form.sn_organisation_address} onChange={F('sn_organisation_address')} placeholder="पता / शहर" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>फ़ोन नम्बर</div>
            <input type="tel" value={form.org_phone_no} onChange={F('org_phone_no')} placeholder="संस्था का फ़ोन नम्बर" style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button block onClick={() => { setShowModal(false); setForm(EMPTY_SOCIAL); setEditId(null); }}
            style={{ borderRadius: 8, height: 42, fontWeight: 600, color: '#6b7280', borderColor: '#d1d5db' }}>
            रद्द करें
          </Button>
          <Button block loading={saving} onClick={saveStatus}
            style={{ borderRadius: 8, height: 42, fontWeight: 700, background: M, borderColor: M, color: G }}>
            {editId ? 'Save करें' : 'जोड़ें'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Payments Tab ──────────────────────────────────────────────────────────────
